import React from 'react';
import { optionToken, optClasses, OptToken } from './optionColor';

/**
 * CRM-grid pill/chip primitives (§2.1 + §9). Deliberately THREE SEPARATE components, NOT one with a
 * boolean (erratum E-2 / §2.1 safety split): the shape IS the affordance, so shape and interactivity must
 * not be collapsible under a later edit. All use logical properties (ps/pe/ms/me) for he-RTL.
 *
 *  - AttributePill : editable attribute — round (rounded-full) + trailing chevron. 44px hit target via a
 *                    transparent INSET wrapper (not padding), visual height stays 24px. Tap → the caller's
 *                    onOpen (a ValueSheet). `ring` variant exists for person-scope disambiguation but
 *                    DEFAULTS OFF (erratum: Leads renders flat pills; the ring's cue is spent for P5 if used
 *                    where every attribute is already person-scope).
 *  - SystemChip    : read-only system/operational state — square (rounded-md), NO chevron, not tappable.
 *  - ActionChip    : lifecycle action — square + outline; performs a confirmed action.
 *  - TagPill       : a tag — tinted + removable ×.
 */

// 24px visual pill; 44px hit area is a transparent inset wrapper around it (not padding on the pill).
const PILL_BASE = 'inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold leading-none max-w-full';
const HIT_WRAP = 'inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-0 bg-transparent align-middle';

export interface AttributePillProps {
  value: string | null | undefined;
  label: string;                  // the field's OWN name (e.g. "Estado") — accessible-name source
                                   // only; AC-D12 (#55): NEVER substituted into the value slot. A
                                   // control that displays a value must never wear its own label as
                                   // that value ("Status"/"Rating" pills on an unset record, caught
                                   // by Q1's fresh-eyes pass).
  emptyText?: string;             // "not set" invitation shown in the value slot; defaults to
                                   // `Set ${label}` — an imperative that can't be misread as a value.
  onOpen?: (anchor: HTMLElement) => void; // tap → open the value sheet/popover (caller decides shape)
  ring?: boolean;                 // person-scope ring — DEFAULT OFF (Leads = flat)
  ariaLabel?: string;
}
export const AttributePill: React.FC<AttributePillProps> = ({ value, label, emptyText, onOpen, ring = false, ariaLabel }) => {
  const tok = optionToken(value);
  const { bg, fg } = optClasses(tok);
  const ringCls = ring ? 'ring-1 ring-inset ring-current/25' : '';
  const invitation = emptyText ?? `Set ${label}`;
  // A11y specified SEPARATELY from display, else the unset state announces "Edit Set <label>" — a
  // double verb from gluing "Edit" onto the empty-invitation text.
  const computedAria = value ? `Edit ${label}: ${value}` : `Set ${label}`;
  return (
    <button
      type="button"
      aria-label={ariaLabel ?? computedAria}
      className={HIT_WRAP}
      // pills must not open the row (§5.1) — stop the tap from bubbling to the card/row.
      onClick={(e) => { e.stopPropagation(); onOpen?.(e.currentTarget); }}
    >
      <span className={`${PILL_BASE} ${bg} ${fg} ${ringCls}`}>
        <span className="truncate" data-testid="attribute-pill-value">{value ?? invitation}</span>
        <span aria-hidden className="opacity-70">&#9662;{/* ▾ */}</span>
      </span>
    </button>
  );
};

export interface SystemChipProps {
  label: string;
  tone?: 'neutral' | 'muted';
  /** WA-2: colour escalation from the bounded §2.2 palette (window countdown, ESTADO). When set, the chip
   *  takes the token's PAIRED tint/fg and drops the hairline border — still square, still not a button, so
   *  the §2.1 shape guarantee ("no writable path here") is unchanged. Omitted → identical to before. */
  token?: OptToken;
  testId?: string;
  title?: string;
}
export const SystemChip: React.FC<SystemChipProps> = ({ label, tone = 'neutral', token, testId, title }) => {
  const paired = token ? optClasses(token) : null;
  const skin = paired
    ? `${paired.bg} ${paired.fg}`
    : `${tone === 'muted' ? 'bg-canvas text-text-muted' : 'bg-canvas text-text-strong'} border border-border-neutral`;
  return (
    // square, no chevron, NOT a button — structurally incapable of reading as editable (INV-1 visual guard).
    <span data-testid={testId} title={title} className={`inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold leading-none ${skin}`}>
      {label}
    </span>
  );
};

export interface ActionChipProps { label: string; onAct: () => void; title?: string; disabled?: boolean; testId?: string }
export const ActionChip: React.FC<ActionChipProps> = ({ label, onAct, title, disabled, testId }) => (
  // square + outline; a lifecycle action (the caller confirms before calling onAct). Same 44px hit
  // target technique as AttributePill — a transparent inset wrapper, not padding, so the visual chip
  // stays 24px while the real click/tap area meets the ≥44px minimum (AC-D9).
  <button
    type="button"
    data-testid={testId}
    title={title}
    disabled={disabled}
    className={`${HIT_WRAP} disabled:opacity-50`}
    onClick={(e) => { e.stopPropagation(); onAct(); }}
  >
    <span className="inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold leading-none bg-surface text-primary border border-primary/40 hover:bg-primary/5 max-w-full">
      <span className="truncate">{label}</span>
    </span>
  </button>
);

export interface TagPillProps { tag: string; onRemove?: (tag: string) => void; testId?: string }
export const TagPill: React.FC<TagPillProps> = ({ tag, onRemove, testId }) => {
  // E-5: tags are BOUNDED-only colour — always slate, never hashed into the 8 status tokens. A hashed tag
  // ("osde"→rose) would collide with a status seed ("Caliente"→rose) and read as if it stated the lead's status.
  const { bg, fg } = optClasses('slate');
  return (
    // min-w-0 + max-w-full: a flex item's default min-width is its content's natural (nowrap) size,
    // which WINS over max-width when the two conflict — max-w-full alone measurably did not stop the
    // overflow (proven: still 420px in a 374px panel with max-w-full alone). min-w-0 removes that
    // floor, so max-w-full can actually cap it and the inner truncate finally has a width to clip
    // against inside a `flex flex-wrap` row. `testId` on THIS (properly constrained) span, not a
    // caller-added wrapper — an unstyled wrapper span breaks the containment chain right back open
    // (measured: the exact same 420px overflow, from a testid-only <span> with no width classes at all).
    <span data-testid={testId} className={`inline-flex items-center gap-1 h-6 ps-2 pe-1 rounded-full text-[11px] font-semibold leading-none min-w-0 max-w-full ${bg} ${fg}`}>
      <span className="truncate">{tag}</span>
      {onRemove && (
        <button type="button" aria-label={`Remove ${tag}`} className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-black/10"
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}>&times;</button>
      )}
    </span>
  );
};
